import InfoBox from '@/components/common/InfoBox';
import {
    AdvancedFilterEgenskapNode,
    AdvancedFilterGroupNode,
    operatorRequiresValue,
    replaceAdvancedFilterNode,
} from '@/domain/advancedFilter';
import { EgenskapTillattVerdi, Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import {
    fetchPossibleAdvancedFilterValues,
    getPossibleAdvancedFilterValuesContextKey,
    PossibleAdvancedFilterValuesResult,
} from '@/middleware/fetchPossibleAdvancedFilterValues';
import { Operator, OperatorE } from '@/middleware/queries';
import { EgenskapsfilterVerdi } from '@/middleware/queries/Egenskapsfilter';
import { statePossibleAdvancedFilterValuesSearchContext } from '@/selectors/selectors';
import { VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import { Dispatch, useDispatch, useSelector } from '@/state/store';
import { UserState } from '@/state/UserState';
import { onMapNavigationStart } from '@/utils/mapNavigationEvents';
import { hasAccess } from '@/utils/Utils';
import * as React from 'react';
import {
    AdvancedFilterTextDropdownInput,
    AdvancedFilterTextDropdownInputHandle,
} from './AdvancedFilterTextDropdownInput';

interface SuggestionState {
    contextKey: string | null;
    result: PossibleAdvancedFilterValuesResult | null;
    loading: 'none' | 'initial' | 'more';
}

const NO_SUGGESTIONS: SuggestionState = {
    contextKey: null,
    result: null,
    loading: 'none',
};

interface Props {
    root: AdvancedFilterGroupNode;
    node: AdvancedFilterEgenskapNode;
    vegobjekttypeData: VegobjekttypeState;
    egenskapstyper: Egenskapstype[];
    onDraftChange: (draft: AdvancedFilterGroupNode | null) => void;
    user: UserState;
    disableSuggestedValueFetch?: boolean;
}

export const EgenskapClauseEditor: React.FC<Props> = ({
    root,
    node,
    vegobjekttypeData,
    egenskapstyper,
    onDraftChange,
    user,
    disableSuggestedValueFetch = false,
}) => {
    const dispatch: Dispatch = useDispatch();
    const data = React.useContext(ConfigAndDataContext)?.data;
    const [localValue, setLocalValue] = React.useState(toInputValue(node.verdi));
    const [suggestionState, setSuggestionState] =
        React.useState<SuggestionState>(NO_SUGGESTIONS);
    const activeRequestRef = React.useRef<AbortController | null>(null);
    const valueInputRef = React.useRef<AdvancedFilterTextDropdownInputHandle>(null);
    const searchContext = useSelector(statePossibleAdvancedFilterValuesSearchContext);
    const operatorMeasureRef = React.useRef<HTMLSpanElement | null>(null);
    const [operatorWidth, setOperatorWidth] = React.useState<number | undefined>(
        undefined
    );

    const operatorOptions = React.useMemo(
        () =>
            node.egenskapstype
                ? operatorsForEgenskapstype(node.egenskapstype).map(operator => ({
                      value: operator,
                      label: Operator.toStringText(operator),
                  }))
                : [],
        [node.egenskapstype]
    );

    const selectedOperatorLabel =
        operatorOptions.find(option => option.value === node.operator)?.label ??
        'Velg operator';
    const suggestionsEnabled =
        !disableSuggestedValueFetch &&
        !!data &&
        !!node.egenskapstype &&
        !node.egenskapstype.erEnum();
    // Identifies everything except this clause's own operator/value, so editing the
    // clause keeps its suggestions while any other relevant change invalidates them.
    const contextKey = React.useMemo(
        () =>
            suggestionsEnabled && data
                ? getPossibleAdvancedFilterValuesContextKey(
                      searchContext,
                      data,
                      vegobjekttypeData,
                      node.id,
                      node.egenskapstype
                  )
                : null,
        [
            data,
            node.egenskapstype,
            node.id,
            searchContext,
            suggestionsEnabled,
            vegobjekttypeData,
        ]
    );
    const suggestions =
        contextKey !== null && suggestionState.contextKey === contextKey
            ? suggestionState
            : NO_SUGGESTIONS;

    React.useEffect(() => {
        setLocalValue(toInputValue(node.verdi));
    }, [node.verdi, node.egenskapstype?.id]);

    const cancelActiveRequest = React.useCallback(() => {
        activeRequestRef.current?.abort();
        activeRequestRef.current = null;
    }, []);

    // Aborted responses are ignored; clear loading so revisiting the context can fetch again.
    const abortSuggestions = React.useCallback(() => {
        if (!activeRequestRef.current) {
            return;
        }
        cancelActiveRequest();
        setSuggestionState(previous =>
            previous.loading === 'none' ? previous : { ...previous, loading: 'none' }
        );
    }, [cancelActiveRequest]);

    const previousContextKeyRef = React.useRef(contextKey);
    React.useEffect(() => {
        if (previousContextKeyRef.current === contextKey) {
            return;
        }
        previousContextKeyRef.current = contextKey;
        abortSuggestions();
        valueInputRef.current?.dismiss();
    }, [abortSuggestions, contextKey]);

    React.useEffect(
        () =>
            onMapNavigationStart(() => {
                abortSuggestions();
                valueInputRef.current?.dismiss();
            }),
        [abortSuggestions]
    );

    React.useEffect(() => cancelActiveRequest, [cancelActiveRequest]);

    React.useLayoutEffect(() => {
        if (!operatorMeasureRef.current) {
            return;
        }

        setOperatorWidth(
            Math.ceil(operatorMeasureRef.current.getBoundingClientRect().width) + 34
        );
    }, [selectedOperatorLabel]);

    const commitNode = React.useCallback(
        (updatedNode: AdvancedFilterEgenskapNode) => {
            onDraftChange(replaceAdvancedFilterNode(root, node.id, () => updatedNode));
        },
        [node.id, onDraftChange, root]
    );

    const handleEgenskapChange = (value: string) => {
        const egenskapstype =
            egenskapstyper.find(et => et.id === Number.parseInt(value, 10)) ?? null;
        commitNode({
            ...node,
            egenskapstype,
            operator: null,
            verdi: null,
        });
        setLocalValue('');
    };

    const handleOperatorChange = (value: string) => {
        const operator = value === '' ? null : (Number.parseInt(value, 10) as OperatorE);
        commitNode({
            ...node,
            operator,
            verdi: operatorRequiresValue(operator) ? node.verdi : null,
        });
        if (!operatorRequiresValue(operator)) {
            setLocalValue('');
        }
    };

    const handleEnumChange = (value: string) => {
        if (!node.egenskapstype) {
            return;
        }

        const enumValue =
            node.egenskapstype.verdiById(Number.parseInt(value, 10)) ?? null;
        commitNode({
            ...node,
            verdi: enumValue,
        });
    };

    const commitValue = React.useCallback(
        (value: string) => {
            if (!node.egenskapstype || !operatorRequiresValue(node.operator)) {
                return;
            }

            if (!value) {
                if (node.verdi === null) {
                    return;
                }
                commitNode({ ...node, verdi: null });
                return;
            }

            if (!getValidationRegex(node.egenskapstype).test(value)) {
                return;
            }

            const parsedValue = parseValue(value, node.egenskapstype);
            if (parsedValue !== null) {
                if (Object.is(parsedValue, node.verdi)) {
                    return;
                }
                commitNode({ ...node, verdi: parsedValue });
            }
        },
        [commitNode, node]
    );

    const requestSuggestions = (continueFrom?: PossibleAdvancedFilterValuesResult) => {
        if (contextKey === null || !node.egenskapstype) {
            return;
        }

        cancelActiveRequest();
        const controller = new AbortController();
        activeRequestRef.current = controller;
        const requestedContextKey = contextKey;
        setSuggestionState(previous => ({
            contextKey: requestedContextKey,
            result: previous.contextKey === requestedContextKey ? previous.result : null,
            loading: continueFrom ? 'more' : 'initial',
        }));

        void dispatch(
            fetchPossibleAdvancedFilterValues(
                vegobjekttypeData,
                node.id,
                node.egenskapstype,
                { continueFrom, signal: controller.signal }
            )
        ).then(result => {
            if (activeRequestRef.current !== controller) {
                return;
            }
            activeRequestRef.current = null;
            setSuggestionState(previous =>
                result.status === 'stale' || previous.contextKey !== requestedContextKey
                    ? { ...previous, loading: 'none' }
                    : { contextKey: requestedContextKey, result, loading: 'none' }
            );
        });
    };

    const handleValueFocus = () => {
        const { result, loading } = suggestions;
        const hasUsableResult =
            result !== null && !(result.status === 'error' && result.values.length === 0);
        if (loading !== 'none' || hasUsableResult) {
            return;
        }
        requestSuggestions();
    };

    const handleLoadMoreSuggestions = () => {
        const { loading, result } = suggestions;
        if (loading !== 'none') {
            return;
        }
        // Without a cursor this is a retry of a failed first batch.
        requestSuggestions(result?.nextCursor ? result : undefined);
    };

    const hasMoreSuggestions = !!suggestions.result?.nextCursor;
    const suggestionsFailed = suggestions.result?.status === 'error';

    return (
        <div className="c-advanced-filter__fields c-advanced-filter__fields--egenskap">
            <div className="c-advanced-filter__field c-advanced-filter__field--property">
                <select
                    className="c-advanced-filter__select"
                    value={node.egenskapstype?.id ?? ''}
                    onChange={event => handleEgenskapChange(event.target.value)}
                    aria-label="Velg egenskap"
                    title={node.egenskapstype?.navn ?? 'Velg egenskap'}
                >
                    <option value="" disabled>
                        Velg egenskap
                    </option>
                    {egenskapstyper
                        .filter(
                            egenskapstype =>
                                !egenskapstype.erStruct() &&
                                !egenskapstype.navn.includes('Assosiert')
                        )
                        .map(egenskapstype => {
                            const isDisabled = !hasAccess(
                                egenskapstype.sensitivitet,
                                user.roles
                            );
                            return (
                                <option
                                    key={egenskapstype.id}
                                    value={egenskapstype.id}
                                    disabled={isDisabled}
                                >
                                    {egenskapstype.navn}{' '}
                                    {isDisabled ? '(Ikke tilgjengelig)' : ''}
                                </option>
                            );
                        })}
                </select>
            </div>

            {node.egenskapstype && (
                <div className="c-advanced-filter__field c-advanced-filter__field--operator">
                    <select
                        className="c-advanced-filter__select"
                        style={
                            operatorWidth ? { width: `${operatorWidth}px` } : undefined
                        }
                        value={node.operator ?? ''}
                        onChange={event => handleOperatorChange(event.target.value)}
                        aria-label="Velg operator"
                        title={selectedOperatorLabel}
                    >
                        <option value="" disabled>
                            Velg operator
                        </option>
                        {operatorOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <span
                        ref={operatorMeasureRef}
                        className="c-advanced-filter__select-measure"
                        aria-hidden="true"
                    >
                        {selectedOperatorLabel}
                    </span>
                </div>
            )}

            {node.egenskapstype && operatorRequiresValue(node.operator) && (
                <>
                    {node.egenskapstype.erEnum() ? (
                        <div className="c-advanced-filter__field c-advanced-filter__field--value">
                            <select
                                className="c-advanced-filter__select"
                                value={
                                    node.verdi instanceof EgenskapTillattVerdi
                                        ? node.verdi.id
                                        : ''
                                }
                                onChange={event => handleEnumChange(event.target.value)}
                                aria-label="Velg verdi"
                                title={
                                    node.verdi instanceof EgenskapTillattVerdi
                                        ? String(node.verdi.verdi)
                                        : 'Velg verdi'
                                }
                            >
                                <option value="" disabled>
                                    Velg verdi
                                </option>
                                {[...node.egenskapstype.tillatte_verdier]
                                    .sort(EgenskapTillattVerdi.comparator)
                                    .map(verdi => (
                                        <option key={verdi.id} value={verdi.id}>
                                            {verdi.verdi}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    ) : (
                        <div className="c-advanced-filter__field c-advanced-filter__field--value c-advanced-filter__field--input c-advanced-filter__field--value-input">
                            <AdvancedFilterTextDropdownInput
                                ref={valueInputRef}
                                value={localValue}
                                options={suggestions.result?.values ?? []}
                                filterOptions={(value, options) =>
                                    filterPossibleValues(
                                        value,
                                        options,
                                        node.egenskapstype
                                    )
                                }
                                onValueChange={value => {
                                    setLocalValue(value);
                                }}
                                onCommitValue={commitValue}
                                onFocus={handleValueFocus}
                                placeholder={getPlaceholder(node.egenskapstype)}
                                title={localValue || getPlaceholder(node.egenskapstype)}
                                ariaLabel="Angi verdi"
                                dropdownAriaLabel="Forslag til filterverdier"
                                inputClassName="c-advanced-filter__input"
                                inputType={valueInputType(node.egenskapstype)}
                                loading={suggestions.loading === 'initial'}
                                hasMoreOptions={hasMoreSuggestions}
                                loadMoreFailed={suggestionsFailed}
                                loadingMoreOptions={suggestions.loading === 'more'}
                                onLoadMoreOptions={handleLoadMoreSuggestions}
                            >
                                {(node.egenskapstype.erTekst() || hasMoreSuggestions) && (
                                    <InfoBox className="info-box-container--egenskapsfilter">
                                        {node.egenskapstype.erTekst() && (
                                            <>
                                                <p>
                                                    Du kan bruke <strong>*</strong> for å
                                                    gjøre vilkårlige søk:
                                                </p>
                                                <ul>
                                                    <li>
                                                        <strong>*ABC*</strong> vil
                                                        filtrere på alt som inneholder{' '}
                                                        <strong>ABC</strong>.
                                                    </li>
                                                    <li>
                                                        <strong>4*meter</strong> vil
                                                        filtrere på alt som slutter på{' '}
                                                        <strong>meter</strong> og starter
                                                        på <strong>4</strong>.
                                                    </li>
                                                </ul>
                                            </>
                                        )}
                                        {hasMoreSuggestions && (
                                            <p>
                                                Forslagene er hentet fra de første
                                                treffene i søket, så flere verdier kan
                                                finnes. Velg <strong>Søk videre</strong>{' '}
                                                nederst i forslagslisten for å hente
                                                flere.
                                            </p>
                                        )}
                                    </InfoBox>
                                )}
                            </AdvancedFilterTextDropdownInput>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

function operatorsForEgenskapstype(egenskapstype: Egenskapstype): OperatorE[] {
    const operators: OperatorE[] = [];
    console.log(egenskapstype);
    if (!egenskapstype.erGeometri()) {
        operators.push(OperatorE.EQ, OperatorE.NEQ);
    }
    if (
        egenskapstype.erNummerisk() ||
        egenskapstype.erDato() ||
        egenskapstype.erKortdato() ||
        egenskapstype.erKlokkeslett()
    ) {
        console.log('Legger til sammenligningsoperatorer');
        operators.push(OperatorE.BEQ, OperatorE.LEQ, OperatorE.LT, OperatorE.GT);
    }
    operators.push(OperatorE.HAS_VALUE, OperatorE.NO_VALUE);
    return operators;
}

function getPlaceholder(egenskapstype: Egenskapstype): string {
    if (egenskapstype.erHeltall()) return 'eks: 1000';
    if (egenskapstype.erFlyttall()) return 'eks: 1000.0';
    if (egenskapstype.erDato()) return 'yyyy-mm-dd';
    if (egenskapstype.erKlokkeslett()) return 'hhmm';
    return '(Tekst)';
}

function getValidationRegex(egenskapstype: Egenskapstype): RegExp {
    if (egenskapstype.erHeltall()) return /^-?\d+$/;
    if (egenskapstype.erFlyttall()) return /^-?\d+[,.]?\d*$/;
    if (egenskapstype.erDato()) return /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12]\d|3[01])$/;
    return /.+$/;
}

function valueInputType(egenskapstype: Egenskapstype): string {
    if (egenskapstype.erNummerisk()) return 'number';
    if (egenskapstype.erDato()) return 'date';
    return 'text';
}

function parseValue(
    value: string,
    egenskapstype: Egenskapstype
): EgenskapsfilterVerdi | null {
    if (!value) return null;
    if (egenskapstype.erHeltall()) return Number.parseInt(value, 10);
    if (egenskapstype.erFlyttall()) return Number.parseFloat(value.replace(',', '.'));
    return value.replace(/^['"]|['"]$/g, '');
}

function toInputValue(value: EgenskapsfilterVerdi | null): string {
    if (value === null || value === undefined) return '';
    if (value instanceof EgenskapTillattVerdi) {
        return String(value.verdi);
    }
    return String(value);
}

function filterPossibleValues(
    inputValue: string,
    possibleFilterValues: string[],
    egenskapstype: Egenskapstype | null
): string[] {
    if (!egenskapstype) {
        return [];
    }

    if (!inputValue) {
        return possibleFilterValues;
    }

    if (egenskapstype.erNummerisk()) {
        return possibleFilterValues.filter(value => value.startsWith(inputValue));
    }

    const loweredInput = inputValue.toLowerCase();
    return possibleFilterValues.filter(value =>
        value.toLowerCase().includes(loweredInput)
    );
}
